import type { LatLng } from './types';

const R = 6371008.8;
const rad = (d: number) => (d * Math.PI) / 180;

export function haversine(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Same estimate as the server's development routing: straight line × 1.35, 24 km/h. */
export const ROAD_FACTOR = 1.35;
export const AVERAGE_KMPH = 24;
export function roadEstimate(a: LatLng, b: LatLng) {
  const meters = haversine(a, b) * ROAD_FACTOR;
  return { meters, seconds: meters / ((AVERAGE_KMPH * 1000) / 3600) };
}

/** Points on a gentle curve from a to b (the server has no road geometry yet, so the route line is drawn, not routed). */
export function curve(a: LatLng, b: LatLng, bend = 0.18, steps = 48): [number, number][] {
  const mx = (a.lng + b.lng) / 2;
  const my = (a.lat + b.lat) / 2;
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  const cx = mx - dy * bend;
  const cy = my + dx * bend;
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const x = (1 - t) ** 2 * a.lng + 2 * (1 - t) * t * cx + t ** 2 * b.lng;
    const y = (1 - t) ** 2 * a.lat + 2 * (1 - t) * t * cy + t ** 2 * b.lat;
    out.push([x, y]);
  }
  return out;
}

export const samePlace = (a: LatLng | null | undefined, b: LatLng | null | undefined, meters = 15) =>
  !!a && !!b && haversine(a, b) <= meters;
